import { Tabs, type TabsProps } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import './CustomerLeadStepQuantities.scss';

import { CustomerLeadStepLayout } from 'src/components/customers/steps/CustomerLeadStepLayout';
import { CustomerLeadStepFrequencyTab } from 'src/components/customers/steps/Quantities/CustomerLeadStepFrequencyTab';
import { CustomerLeadStepInitialRequestTab } from 'src/components/customers/steps/Quantities/CustomerLeadStepInitialRequestTab';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import {
  PRODUCT_SPECIFICATION_FIELD_GROUP_MAPPER,
  ProductSpecificationFieldGroup,
} from 'src/types/reference/ProductSpecificationFieldGroup';

const CustomerLeadStepQuantitiesSubSteps: Partial<Record<string, string>> = {
  [ProductSpecificationFieldGroup.InitialRequest]: ProductSpecificationFieldGroup.Frequency,
};

type CustomerLeadStepQuantitiesProps = {
  customerProductLeadId: string;
};

export function CustomerLeadStepQuantities({ customerProductLeadId }: Readonly<CustomerLeadStepQuantitiesProps>) {
  const [currentTab, setCurrentTab] = useState<string>(ProductSpecificationFieldGroup.InitialRequest);

  const { customerLeadProduct, customerLeadProductSpecification, setCustomerStepGuideKey } =
    useContext(CustomersContext);

  const handleSubmit = useCallback(() => {
    const newSubStep = CustomerLeadStepQuantitiesSubSteps[currentTab];

    const moveToNextStep = newSubStep === undefined;

    if (!moveToNextStep) {
      setCurrentTab(newSubStep);
    }

    // Go to next step if empty
    return moveToNextStep;
  }, [currentTab]);

  const tabs: Exclude<TabsProps['items'], undefined> = useMemo(
    () => [
      {
        key: ProductSpecificationFieldGroup.InitialRequest,
        label: PRODUCT_SPECIFICATION_FIELD_GROUP_MAPPER[ProductSpecificationFieldGroup.InitialRequest],
        children: (
          <CustomerLeadStepInitialRequestTab customerProductLeadId={customerProductLeadId} onSubmit={handleSubmit} />
        ),
      },
      {
        key: ProductSpecificationFieldGroup.Frequency,
        label: PRODUCT_SPECIFICATION_FIELD_GROUP_MAPPER[ProductSpecificationFieldGroup.Frequency],
        children: (
          <CustomerLeadStepFrequencyTab customerProductLeadId={customerProductLeadId} onSubmit={handleSubmit} />
        ),
      },
    ],
    [customerProductLeadId, handleSubmit]
  );

  const handleTabChange = useCallback(
    (newActiveKey: string) => {
      setCustomerStepGuideKey(
        newActiveKey === ProductSpecificationFieldGroup.Quantities
          ? ProductSpecificationFieldGroup.Quantities
          : newActiveKey === ProductSpecificationFieldGroup.ProductDetails
            ? ProductSpecificationFieldGroup.ProductDetails
            : newActiveKey === ProductSpecificationFieldGroup.ShippingDetails
              ? ProductSpecificationFieldGroup.ShippingDetails
              : null
      );

      setCurrentTab(newActiveKey as ProductSpecificationFieldGroup);
    },
    [setCustomerStepGuideKey]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: false
  useEffect(() => {
    setCustomerStepGuideKey(
      currentTab === ProductSpecificationFieldGroup.Quantities
        ? ProductSpecificationFieldGroup.Quantities
        : currentTab === ProductSpecificationFieldGroup.ProductDetails
          ? ProductSpecificationFieldGroup.ProductDetails
          : currentTab === ProductSpecificationFieldGroup.ShippingDetails
            ? ProductSpecificationFieldGroup.ShippingDetails
            : null
    );
  }, [currentTab]);

  if (!customerLeadProduct || !customerLeadProductSpecification) {
    return null;
  }

  return (
    <CustomerLeadStepLayout>
      <div className="customer-lead-step-quantities__container">
        <Tabs
          items={tabs}
          animated={false}
          destroyInactiveTabPane={false}
          activeKey={currentTab}
          onChange={handleTabChange}
        />
      </div>
    </CustomerLeadStepLayout>
  );
}
